/**
 * History polyfill for srcdoc iframes.
 *
 * In about:srcdoc context window.history is non-functional.
 * This module patches window.history so that route changes are tracked
 * and communicated to the parent host.
 *
 * window.location is NOT patched (non-configurable in srcdoc iframes).
 * Apps inside srcdoc should use createMemoryHistory(), not createWebHistory().
 *
 * Must run synchronously before any app code.
 *
 * Internal paths are stored WITH the base path prefix (e.g. "/my-app/dist/about")
 * so the router sees the full path it expects. When communicating with the parent host,
 * paths are stripped to app-relative form (e.g. "/about").
 */

import { IFRAME_MESSAGE_TYPE, IFrameMessageType } from '@shared/iframe-messages'

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface HistoryEntry {
  state: any
  title: string
  url: string // full path including base prefix, e.g. "/my-app/dist/about"
}

let stack: HistoryEntry[] = [{ state: null, title: '', url: '/' }]
let currentIndex = 0
let isActive = false
let historyWarned = false

// Loop prevention
let routeChangeCallback: ((path: string) => void) | null = null
let lastSentPath: string | null = null
let updatingFromParent = false

// Base path from <base> tag — used to translate between full internal paths
// and app-relative paths for parent communication
let basePath: string | null = null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cloneState(state: any): any {
  if (state === null || state === undefined)
    return null
  try {
    return structuredClone(state)
  }
  catch {
    try {
      return JSON.parse(JSON.stringify(state))
    }
    catch {
      return null
    }
  }
}

/**
 * Strip the base path prefix from an internal path to get an app-relative path.
 * E.g. "/my-app/dist/about" → "/about"
 * Used when sending paths to the parent host.
 */
function stripBasePath(path: string): string {
  if (!basePath)
    return path
  if (path.startsWith(basePath)) {
    const stripped = path.slice(basePath.length)
    return stripped.startsWith('/') ? stripped : `/${stripped}`
  }
  // Handle base path without trailing slash
  const baseNoSlash = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
  if (path === baseNoSlash)
    return '/'
  return path
}

/**
 * Add the base path prefix to an app-relative path.
 * E.g. "/about" → "/my-app/dist/about"
 * Used when receiving paths from the parent host.
 */
function addBasePath(path: string): string {
  if (!basePath)
    return path
  const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
  return base + (path.startsWith('/') ? path : `/${path}`)
}

/**
 * HACK (vue-router): Vue Router with createWebHistory() reads the unpatchable
 * window.location.pathname which returns "srcdoc" in about:srcdoc iframes.
 * It then concatenates basePath with "srcdoc", producing e.g. "/navi/distsrcdoc".
 * This is always an invalid path — detect it so replaceState can ignore it.
 */
function isSrcdocNoise(path: string): boolean {
  if (!basePath)
    return false
  const baseNoSlash = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
  return path === `${baseNoSlash}srcdoc` || path === `${baseNoSlash}/srcdoc`
}

/**
 * Notify the parent about a route change.
 * Strips the base path prefix so parent only sees app-relative paths.
 */
function notifyRouteChange(url: string) {
  if (updatingFromParent)
    return
  const outgoing = stripBasePath(url)
  if (outgoing === lastSentPath)
    return
  lastSentPath = outgoing
  if (routeChangeCallback) {
    routeChangeCallback(outgoing)
  }
}

function firePopstate() {
  const state = cloneState(stack[currentIndex]?.state ?? null)
  setTimeout(() => {
    window.dispatchEvent(new PopStateEvent('popstate', { state }))
  }, 0)
}

function warnHistoryUsage() {
  if (historyWarned)
    return
  historyWarned = true
  console.error(
    '[wippy-proxy] Your app used polyfilled window.history inside srcdoc iframe but window.location is NOT patchable. '
    + 'Apps should use createMemoryHistory() instead of createWebHistory() for correct routing.',
  )
}

// ---------------------------------------------------------------------------
// History implementation
// ---------------------------------------------------------------------------

function createVirtualHistory() {
  let _scrollRestoration: ScrollRestoration = 'auto'

  return {
    get length() {
      return stack.length
    },

    get state() {
      return cloneState(stack[currentIndex]?.state ?? null)
    },

    get scrollRestoration(): ScrollRestoration {
      return _scrollRestoration
    },
    set scrollRestoration(value: ScrollRestoration) {
      _scrollRestoration = value
    },

    pushState(state: any, title: string, url?: string | null) {
      warnHistoryUsage()
      const normalizedUrl = normalizePath(url)
      // Truncate forward history
      stack = stack.slice(0, currentIndex + 1)
      stack.push({ state: cloneState(state), title, url: normalizedUrl })
      currentIndex = stack.length - 1
      notifyRouteChange(normalizedUrl)
    },

    replaceState(state: any, title: string, url?: string | null) {
      warnHistoryUsage()
      const normalizedUrl = normalizePath(url)
      // HACK: Vue Router with createWebHistory() reads unpatchable window.location
      // in srcdoc iframes and builds URLs like "/navi/distsrcdoc" or "/navi/dist/srcdoc"
      // by concatenating basePath with the "srcdoc" from about:srcdoc. Ignore these.
      if (isSrcdocNoise(normalizedUrl))
        return
      stack[currentIndex] = { state: cloneState(state), title, url: normalizedUrl }
      notifyRouteChange(normalizedUrl)
    },

    go(delta?: number) {
      warnHistoryUsage()
      if (delta === undefined || delta === null)
        delta = 0
      if (delta === 0) {
        firePopstate()
        return
      }
      const newIndex = Math.max(0, Math.min(stack.length - 1, currentIndex + delta))
      if (newIndex !== currentIndex) {
        currentIndex = newIndex
        notifyRouteChange(stack[currentIndex].url)
        firePopstate()
      }
    },

    back() {
      this.go(-1)
    },

    forward() {
      this.go(1)
    },
  }
}

// ---------------------------------------------------------------------------
// Helpers (path normalization)
// ---------------------------------------------------------------------------

function normalizePath(url: string | null | undefined): string {
  if (!url)
    return stack[currentIndex]?.url || '/'
  if (url.startsWith('/'))
    return url
  // Resolve relative to current path
  try {
    const base = new URL(stack[currentIndex]?.url || '/', 'https://virtual.host')
    const resolved = new URL(url, base)
    return resolved.pathname + resolved.search + resolved.hash
  }
  catch {
    return `/${url}`
  }
}

// ---------------------------------------------------------------------------
// Parent message listener (UrlWasUpdatedInParent)
// ---------------------------------------------------------------------------

function listenForParentMessages() {
  window.addEventListener('message', (event) => {
    let data: any
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
    }
    catch {
      return
    }

    if (
      data?.type === IFRAME_MESSAGE_TYPE
      && data?.action === IFrameMessageType.UrlWasUpdatedInParent
    ) {
      const newPath = data.path
      if (!newPath)
        return
      const rawNormalized = newPath.startsWith('/') ? newPath : `/${newPath}`
      // Parent sends app-relative paths — add base prefix for the router
      const normalized = addBasePath(rawNormalized)
      const currentPath = stack[currentIndex]?.url || '/'

      if (normalized !== currentPath) {
        updatingFromParent = true
        // Update current entry (replaceState semantics — parent is just syncing, not pushing)
        stack[currentIndex] = { state: null, title: '', url: normalized }
        lastSentPath = rawNormalized // prevent echo (stored as stripped)
        // Fire popstate so app routers react
        setTimeout(() => {
          window.dispatchEvent(
            new PopStateEvent('popstate', { state: stack[currentIndex]?.state ?? null }),
          )
          updatingFromParent = false
        }, 0)
      }
    }
  })
}

// ---------------------------------------------------------------------------
// Installation
// ---------------------------------------------------------------------------

export function installHistoryPolyfill(): void {
  // Only activate in srcdoc context
  if (typeof window === 'undefined')
    return

  let isSrcdoc = false
  try {
    isSrcdoc = window.location.href === 'about:srcdoc'
  }
  catch {
    // If reading location throws, we're likely in a restricted context — try to polyfill
    isSrcdoc = true
  }

  if (!isSrcdoc)
    return

  // Guard against double-install
  if (isActive)
    return
  isActive = true

  // Read base path directly from <base> tag in DOM (already present when proxy.js runs)
  const baseElement = document.querySelector('base')
  if (baseElement) {
    const baseHref = baseElement.getAttribute('href')
    if (baseHref) {
      setHistoryBasePath(baseHref)
    }
  }

  // Set initial stack entry with base path if available
  if (basePath) {
    stack = [{ state: null, title: '', url: basePath }]
  }

  const virtualHistory = createVirtualHistory()

  // Patch window.history
  try {
    Object.defineProperty(window, 'history', {
      value: virtualHistory,
      writable: true,
      configurable: true,
    })
  }
  catch (e) {
    console.warn('[history-polyfill] Failed to patch window.history:', e)
  }

  // NOTE: window.location is NOT patched — it's non-configurable in srcdoc iframes.
  // Use navigateVueRouter() for initial path and parent-driven navigation instead.

  // Start listening for parent messages
  listenForParentMessages()
}

// ---------------------------------------------------------------------------
// Post-init hooks (called after async config resolves)
// ---------------------------------------------------------------------------

/**
 * Set the initial app route. Receives an app-relative path (e.g. "/about")
 * and stores it with the base prefix if configured.
 */
export function setInitialPath(path: string): void {
  if (!isActive)
    return
  const normalized = path.startsWith('/') ? path : `/${path}`
  // Add base prefix — internal stack stores full paths
  const fullPath = addBasePath(normalized)
  stack = [{ state: null, title: '', url: fullPath }]
  currentIndex = 0
  lastSentPath = normalized // don't echo initial path back to parent (stored as stripped)
}

/**
 * Configure the base path from the <base> tag.
 * Extracts the pathname to use as prefix for stripping/adding.
 *
 * @param baseUrl Full base URL, e.g. "https://statics.example.com/my-app/dist/"
 */
export function setHistoryBasePath(baseUrl: string): void {
  try {
    const parsed = new URL(baseUrl)
    basePath = parsed.pathname.endsWith('/') ? parsed.pathname : `${parsed.pathname}/`
  }
  catch {
    basePath = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  }
}

export function isHistoryPolyfillActive(): boolean {
  return isActive
}

export function onRouteChange(callback: (path: string) => void): void {
  routeChangeCallback = callback
}

// ---------------------------------------------------------------------------
// History guard — stubs history methods so they don't crash in srcdoc
// ---------------------------------------------------------------------------

const HISTORY_ERROR = '[wippy-proxy] Your app used window.history inside an srcdoc iframe. '
  + 'window.history and window.location are non-functional in srcdoc context. '
  + 'Use createMemoryHistory() instead of createWebHistory() for correct routing.'

let guardWarned = false

function warnOnce() {
  if (guardWarned)
    return
  guardWarned = true
  console.error(HISTORY_ERROR)
}

/**
 * Install a guard that replaces window.history methods with no-ops
 * that log an error directing apps to use memory routing.
 * Only activates in srcdoc iframes.
 */
export function installHistoryGuard(): void {
  if (typeof window === 'undefined')
    return

  let isSrcdoc = false
  try {
    isSrcdoc = window.location.href === 'about:srcdoc'
  }
  catch {
    isSrcdoc = true
  }

  if (!isSrcdoc)
    return

  const noop = () => {
    warnOnce()
  }

  try {
    Object.defineProperty(window, 'history', {
      value: {
        get length() { return 1 },
        get state() { return null },
        get scrollRestoration() { return 'auto' as ScrollRestoration },
        set scrollRestoration(_v: ScrollRestoration) {},
        pushState: noop,
        replaceState: noop,
        go: noop,
        back: noop,
        forward: noop,
      },
      writable: true,
      configurable: true,
    })
  }
  catch (e) {
    console.warn('[wippy-proxy] Failed to install history guard:', e)
  }
}
